/**
 * What the Traits list shows right now (CT-5715). One value, not six booleans
 * whose interaction has to be re-derived at every reading.
 *
 * `noneYet` and `noResults` are both "no rows" and are deliberately different
 * messages: telling someone to clear their filters when the person simply has
 * no traits yet is noise, and offering "add a note" to someone who filtered
 * them all away is worse.
 */
export const TRAITS_LIST_STATE = Object.freeze({
  LOADING: 'loading',
  ACCESS_DENIED: 'accessDenied',
  ERROR: 'error',
  /** A 200 with nothing the viewer may see — not a 403. */
  RESTRICTED: 'restricted',
  /** Nothing has been learned about this person yet. */
  NONE_YET: 'noneYet',
  /** There are traits, but none the chip, filters or search let through. */
  NO_RESULTS: 'noResults',
  ROWS: 'rows',
});

/**
 * The order matters and is the product rule:
 *
 * A failure outranks emptiness — an empty list drawn over a failed request
 * claims the person has no traits when nobody asked successfully. `restricted`
 * outranks the narrowing states for the same reason: the viewer cannot clear
 * their way out of it, so offering them "clear filters" would be a dead end.
 *
 * The unverified group counts as content: a list whose only traits are
 * unverified is not empty, it just has nothing above the fold.
 *
 * @param {object} args
 * @param {boolean} args.loading
 * @param {boolean} args.accessDenied
 * @param {string|null} args.error
 * @param {number} args.itemCount
 * @param {number} args.unverifiedCount
 * @param {boolean} args.restricted
 * @param {boolean} args.narrowed — a chip, a filter or a search is on.
 * @returns {string} one of `TRAITS_LIST_STATE`.
 */
export const traitsListState = ({
  loading,
  accessDenied,
  error,
  itemCount,
  unverifiedCount,
  restricted,
  narrowed,
}) => {
  if (loading) return TRAITS_LIST_STATE.LOADING;
  if (accessDenied) return TRAITS_LIST_STATE.ACCESS_DENIED;
  if (error) return TRAITS_LIST_STATE.ERROR;

  if (itemCount > 0 || unverifiedCount > 0) return TRAITS_LIST_STATE.ROWS;
  if (restricted) return TRAITS_LIST_STATE.RESTRICTED;
  if (narrowed) return TRAITS_LIST_STATE.NO_RESULTS;
  return TRAITS_LIST_STATE.NONE_YET;
};

/**
 * The chips are hidden only where they would be a lie: there is nothing to
 * narrow, and nothing the user did caused that.
 */
export const showsTraitQuickFilters = (state) =>
  state !== TRAITS_LIST_STATE.RESTRICTED && state !== TRAITS_LIST_STATE.NONE_YET;

/** The request has come back, one way or another. */
export const isTraitsListSettled = (state) =>
  state !== TRAITS_LIST_STATE.LOADING &&
  state !== TRAITS_LIST_STATE.ERROR &&
  state !== TRAITS_LIST_STATE.ACCESS_DENIED;
